<template>
	<div class="mt-lg-10 mt-14 mb-5">
		<loading-overlay :active.sync="loading"/>

		<div class="d-flex flex-column flex-lg-row align-items-start justify-content-between mb-5">
			<div class="w-100 w-lg-auto">
				<my-dao-power-component ref="myDaoPowerComponent" :dao-power="daoPower" :puzzle-points="puzzlePoints" class="d-none d-lg-block"/>
				<metrics-progress-component :metrics="metrics"/>
                <my-dao-power-component ref="myDaoPowerComponent" :dao-power="daoPower" :puzzle-points="puzzlePoints" class="d-lg-none"/>
			</div>

			<div class="text-lg-end mt-6 mt-lg-0">
                <template v-if="primaryMarketClosed">
                    <i>All random pPFPs have been sold out. <br/>You can now place bids and offers on the secondary market.</i>
                </template>

				<template v-else>
                    <div class="mb-2">
                        <p class="mb-0 justify-content-lg-end fw-bold fs-24 d-flex align-items-center">
                            <svg class="me-2 bg-white text-black rounded-circle" height="30" width="30">
                                <use xlink:href="#hada-icon"></use>
                            </svg>
                            <span>{{ roundOff(pricePerPiece, 0) }}</span>
                        </p>
                        <p v-if="userHasVoucher" class="mt-1 text-start text-lg-end fs-12 mb-2">
                            <i>Giveaway voucher applied</i>
                        </p>
                        <p v-else-if="wtfHolder" class="mt-1 text-start text-lg-end fs-12 mb-2">
                            <i>5% WTF owner discount applied</i>
                        </p>
                    </div>

                    <div class="mb-4">
                        <button :disabled="loading||loadingTokenBalance"
                                class="btn btn-success text-capitalize fs-24 fw-semibold rounded-1 py-2 px-3 d-flex align-items-center ms-lg-auto"
                                @click="inquireForRandomPiece()">
                            <spinner-component v-if="loadingTokenBalance" color="text-black" class="me-2"/>
                            <span class="d-md-none">Buy Puzzle Piece</span>
                            <span class="d-none d-md-block">
                                Buy Random Puzzle Piece
                            </span>
                        </button>
                    </div>
                    <p v-if="saleProgress" class="mb-0 mt-4 mt-lg-0 px-2 py-1 rounded-1 bg-primary border border-primary-200 fs-14 fs-md-18 text-secondary">
                        <i>{{ saleProgress }}</i>
                    </p>
                </template>
			</div>
		</div>

		<!--Buy Puzzle Piece Modal-->
		<div id="purchasePuzzlePieceModal" aria-hidden="true" aria-labelledby="purchasePuzzlePieceModalLabel"
		     class="modal fade" data-bs-backdrop="static" data-bs-keyboard="false" tabindex="-1">
			<div class="modal-dialog modal-dialog-centered">
				<div class="modal-content rounded-4 bg-primary">
					<div class="modal-header p-4 border-0 justify-content-between align-items-start">
                        <div>
                            <h5 id="purchasePuzzlePieceModalLabel" class="modal-title mb-3 fs-24 fw-bold">
                                Puzzle Piece Purchase
                            </h5>
                            <p v-if="!insufficientFunds" class="mb-0 text-grey-200">
                                You are about to buy a random puzzle piece for {{ roundOff(form.saleOffer.amount) }} HADA.
                            </p>
                        </div>
                        <svg width="24" height="24" class="flex-shrink-0" data-bs-dismiss="modal"
                             type="button">
                            <use xlink:href="#close-icon"></use>
                        </svg>
					</div>

					<div class="modal-body text-center">
                        <template v-if="insufficientFunds">
                            <p class="mb-0 text-secondary">
                                You do not have enough funds in your account to buy a random puzzle piece. Kindly top up your account to proceed.
                            </p>
                        </template>
						<template v-else>
                            <div class="selected-puzzle-piece rounded overflow-hidden mb-8 mx-auto" v-if="form.randomPiece.file_path">
                                <img :src="$page.props.environment === 'production' ? `/detailed_nfts${form.randomPiece.file_path}` : `/optimized_nfts${form.randomPiece.file_path}`" alt="Selected puzzle piece" class="h-100 w-100"/>
                            </div>

							<div :class="{'d-flex align-items-center': wtfHolder}">
								<div :class="{'mx-auto': wtfHolder}">
									<h3 class="fs-24 fw-bold text-success text-center">
                                        HADA {{ roundOff(form.saleOffer.amount) }}
                                    </h3>
									<p v-if="userHasVoucher" class="mb-0">Giveaway voucher applied.</p>
									<p v-else-if="wtfHolder" class="mb-0">5% WTF owner discount applied.</p>
								</div>
							</div>
						</template>
					</div>

					<div class="modal-footer border-0 py-4 px-4">
						<button :disabled="loading" class="btn btn-primary-200 text-white whitespace-nowrap py-3 px-4 col-md-5 rounded-1 fs-18 fw-bold mx-auto" data-bs-dismiss="modal" type="button">
							Cancel
						</button>
						<a v-if="insufficientFunds"
						   :class="['btn btn-success text-black whitespace-nowrap py-3 px-4 col-md-5 rounded-1 fs-18 fw-bold mx-auto', {'disabled': loading || inTestEnvironments}]"
						   href="https://sologenic.org/trade?market=4841444100000000000000000000000000000000%2BrsR5JSisuXsbipP6sGdKdz5agjxn8BhHUC%2FXRP&network=mainnet"
						   target="_blank">
							Buy HADA
						</a>
						<button v-else :disabled="loading"
                                class="btn btn-success text-black whitespace-nowrap py-3 px-4 col-md-5 rounded-1 fs-18 fw-bold mx-auto" type="button"
                                @click="purchaseRandomPiece()">
							Buy Now
						</button>
					</div>
				</div>
			</div>
		</div>

        <!--Accept transaction modal-->
        <div id="acceptTransactionModal" aria-hidden="true" aria-labelledby="acceptTransactionModalLabel"
             class="modal fade" data-bs-backdrop="static" data-bs-keyboard="false" tabindex="-1">
            <div class="modal-dialog modal-dialog-centered modal-fullscreen-sm-down">
                <div class="modal-content rounded-4 bg-primary">
                    <div class="modal-header p-4 border-0 justify-content-between align-items-start">
                        <div>
                            <h5 class="modal-title mb-3 fs-24 fw-bold text-capitalize" id="acceptTransactionModalLabel">
                                Random Puzzle Piece Purchase
                            </h5>

                            <p class="mb-0 text-grey-200">
                                A notification has been sent to your mobile application. Open the Xumm
                                application on your device to complete the transaction.
                            </p>
                        </div>
                        <svg width="24" height="24" class="flex-shrink-0" data-bs-dismiss="modal"
                             type="button">
                            <use xlink:href="#close-icon"></use>
                        </svg>
                    </div>
                    <div class="modal-body p-4 pb-6 text-center">
                        <template v-if="qrCode">
                            <p>Or scan the QR code below:</p>
                            <img :src="qrCode" :alt="signLink"/>
                        </template>

                        <template v-if="signLink">
                            <p class="mt-5 mb-0 text-white">You can use the following link to complete the same transaction: <a class="text-white" :href="signLink">{{ signLink }}</a></p>
                        </template>
                    </div>
                </div>
            </div>
        </div>

		<hr class="border-primary-100">
	</div>
</template>

<script>
import MetricsProgressComponent from './MetricsProgressComponent'
import MyDaoPowerComponent from './MyDaoPowerComponent'
import SpinnerComponent from './Common/SpinnerComponent'
import HelperFunctions from '@/Mixins/HelperFunctions'
import {EventBus} from '@/Mixins/event-bus'

export default {
	name: 'PuzzleProgressComponent',

	components: {
		MetricsProgressComponent,
		MyDaoPowerComponent,
		SpinnerComponent
	},

	props: {
		tokens: Object,
	},

	data() {
		return {
			loading: false,
			loadingTokenBalance: false,
			primaryMarketClosed: false,
			form: {
				randomPiece: {},
				saleOffer: {},
			},
			qrCode: null,
			signLink: null,
			wtfHolder: false,
			pricePerPiece: 1000000,
			userHasVoucher: false,
			insufficientFunds: false,
			daoPower: 0,
			puzzlePoints: 0,
			metrics: {},
			saleProgress: ''
		}
	},

	computed: {
		inTestEnvironments() {
			return this.$page.props.environment !== 'production'
		}
	},

	mixins: [HelperFunctions],

	watch: {
		tokens: {
			handler(newValue) {
				if (typeof newValue.wtf !== 'undefined' && newValue.wtf.balance > 0) {
					this.pricePerPiece = this.pricePerPiece * 0.95
					this.wtfHolder = true
				}
				this.loadingTokenBalance = Object.keys(this.tokens).length === 0
			},
			deep: true,
			immediate: true
		}
	},

	created() {
		this.getUserData()
		this.checkSaleProgress()
	},

	methods: {
		getUserData() {
			axios.post(route('user.meta')).then(response => {
				this.daoPower = response.data.daoPower
				this.puzzlePoints = response.data.puzzlePoints
				this.metrics = response.data.metrics
				this.userHasVoucher = response.data.hasVoucher
				if (response.data.hasVoucher) {
					this.pricePerPiece = 1
				} else {
					this.pricePerPiece = 1000000
					if (this.tokens.wtf && this.tokens.wtf.balance > 0) {
						this.pricePerPiece = this.pricePerPiece * 0.95
						this.wtfHolder = true
					}
				}
			}).catch(error => {
				Toast.fire({
					icon: 'error',
					title: error.response.data.message
				})
			})
		},

		checkSaleProgress() {
			axios.get(route('nft.collection.sale-progress')).then(response => {
				this.saleProgress = response.data.sale_progress
				this.primaryMarketClosed = response.data.primary_market_closed
			})
		},

		inquireForRandomPiece() {
			this.loading = true
			axios.post(route('nft.collection.inquire-random-piece')).then((response) => {
				this.loading = false
				this.form.randomPiece = response.data.nftPiece
				this.form.saleOffer = response.data.saleOffer
				this.insufficientFunds = response.data.insufficientFunds
				$('#purchasePuzzlePieceModal').modal('show')
				this.$inertia.reload()
			}).catch(error => {
				this.loading = false
				Toast.fire({
					icon: 'error',
					title: error.data.message
				})
			})
		},

		purchaseRandomPiece() {
			this.loading = true
			$('#purchasePuzzlePieceModal').modal('hide')
			axios.post(route('nft.collection.buy-random-piece'), this.form).then((response) => {
				this.qrCode = response.data.qrCode
				this.signLink = response.data.signLink
				this.loading = false
				$('#acceptTransactionModal').modal('show')
				let transactionId = response.data.transactionId
				let intervalId = setInterval(() => {
					this.checkTransactionStatus(transactionId, intervalId)
				}, 4000)
			}).catch(error => {
				this.loading = false
				Toast.fire({
					icon: 'error',
					title: error.data.message
				})
			})
		},

		checkTransactionStatus(pendingTransactionId, interval) {
			axios.get(route('check-transaction-status', pendingTransactionId)).then(response => {
				if (!this.is_null(response.data.status)) {
					$('#acceptTransactionModal').modal('hide')
					Toast.fire({
						icon: response.data.status,
						title: response.data.message
					})
					if(response.data.status === 'success') {
						window.location.href = `/nft/collection/${this.form.saleOffer.collectible_id}/puzzle?collectible=${this.form.saleOffer.collectible_id}`
					} else {
						this.getUserData()
						this.clearForm()
						clearInterval(interval)
						EventBus.$emit('buyRandomPiece', response.data.status)
						this.$inertia.reload()
					}
					axios.post(route('delete-pending-transaction', pendingTransactionId))
				}
			})
		},

		clearForm() {
			this.form.randomPiece = {}
			this.form.saleOffer = {}
		}
	}
}
</script>
